// src/Components/Auth/RegisterForm.jsx
// FRONT OFFICE - Customer registration component

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaUser, FaEnvelope, FaLock, FaEye, FaEyeSlash, FaPhone } from 'react-icons/fa';
import { registerWithEmail } from '../../services/AuthService';
import './Registration.css';

const RegisterForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    
    if (Object.keys(validationErrors).length === 0) {
      try {
        setLoading(true);
        setRegisterError('');
        
        const { name, email, phone, password } = formData;
        await registerWithEmail(email, password, name, phone);
        
        // On successful registration, redirect to login page
        navigate('/login', { state: { message: 'Registration successful! Please check your email to verify your account.' } });
      } catch (error) {
        console.error('Registration failed:', error);
        setRegisterError(error.message || 'Registration failed. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      setErrors(validationErrors);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    }
    
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    return errors;
  };

  return (
    <div className="register-container">
      <div className="register-wrapper">
        <div className="register-header">
          <h2>Create Account</h2>
          <p>Join our pharmacy family</p>
        </div>

        {registerError && <div className="register-error">{registerError}</div>}

        <form onSubmit={handleSubmit} className="register-form">
          <div className={`form-group ${errors.name ? 'error' : ''}`}>
            <label htmlFor="name">Full Name</label>
            <div className="input-wrapper">
              <FaUser className="input-icon" />
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={handleChange}
              />
            </div>
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className={`form-group ${errors.email ? 'error' : ''}`}>
            <label htmlFor="email">Email Address</label>
            <div className="input-wrapper">
              <FaEnvelope className="input-icon" />
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className={`form-group ${errors.phone ? 'error' : ''}`}>
            <label htmlFor="phone">Phone Number</label>
            <div className="input-wrapper">
              <FaPhone className="input-icon" />
              <input
                type="tel"
                id="phone"
                name="phone"
                placeholder="Enter your phone number"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>
            {errors.phone && <span className="error-message">{errors.phone}</span>}
          </div>

          <div className={`form-group ${errors.password ? 'error' : ''}`}>
            <label htmlFor="password">Password</label>
            <div className="input-wrapper">
              <FaLock className="input-icon" />
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                placeholder="Create a strong password"
                value={formData.password}
                onChange={handleChange}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {errors.password && <span className="error-message">{errors.password}</span>}
          </div>

          <div className={`form-group ${errors.confirmPassword ? 'error' : ''}`}>
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="input-wrapper">
              <FaLock className="input-icon" />
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onChange={handleChange}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
          </div>

          <div className="newsletter-option">
            <input type="checkbox" id="newsletter" name="newsletter" />
            <label htmlFor="newsletter">Subscribe to our newsletter</label>
          </div>

          <div className="privacy-terms">
            <p>
              By creating an account, you agree to our <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>.
            </p>
          </div>

          <button 
            type="submit" 
            className="register-button"
            disabled={loading}
          >
            {loading ? 'Creating Account...' : 'Register'}
          </button>

          <div className="login-link">
            Already have an account? <Link to="/login">Login here</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterForm;





























// // src/pages/Registration/Registration.jsx

// import React, { useState } from 'react';
// import { Link } from 'react-router-dom';
// import './Registration.css';

// const RegisterForm = () => {
//   const [email, setEmail] = useState('');
//   const [subscribe, setSubscribe] = useState(true);
//   const [errors, setErrors] = useState({});

//   const handleSubmit = (e) => {
//     e.preventDefault();
//     const validationErrors = validateForm();
//     if (Object.keys(validationErrors).length === 0) {
//       // Handle registration logic
//       console.log('Registration submitted:', { email, subscribe });
//     } else {
//       setErrors(validationErrors);
//     }
//   };

//   const validateForm = () => {
//     const errors = {};
//     if (!email.trim()) {
//       errors.email = 'Email is required';
//     } else if (!/\S+@\S+\.\S+/.test(email)) {
//       errors.email = 'Email is invalid';
//     }
//     return errors;
//   };

//   return (
//     <div className="register-container">
//       <div className="register-wrapper">
//         <h1 className="register-title">REGISTER</h1>
        
//         <form onSubmit={handleSubmit} className="register-form">
//           <div className={`form-group ${errors.email ? 'error' : ''}`}>
//             <h2 className="form-label">Email address *</h2>
//             <input
//               type="email"
//               value={email}
//               onChange={(e) => setEmail(e.target.value)}
//               placeholder="Enter your email address"
//               className="register-input"
//             />
//             {errors.email && <span className="error-message">{errors.email}</span>}
//           </div>

//           <div className="form-divider"></div>

//           <p className="password-note">
//             A link to set a new password will be sent to your email address.
//           </p>

//           <div className="newsletter-option">
//             <label className="newsletter-label">
//               <input
//                 type="checkbox"
//                 checked={subscribe}
//                 onChange={() => setSubscribe(!subscribe)}
//                 className="newsletter-checkbox"
//               />
//               <span className="newsletter-text">❤ Subscribe to our newsletter</span>
//             </label>
//           </div>

//           <p className="privacy-notice">
//             Your personal data will be used to support your experience throughout this website, 
//             to manage access to your account, and for other purposes described in our privacy policy.
//           </p>

//           <div className="form-divider"></div>

//           <button type="submit" className="register-button">
//             REGISTER
//           </button>
//         </form>
//       </div>
//     </div>
//   );
// };

// export default RegisterForm;